import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Briefcase,
  Inbox,
  LogOut,
  Plus,
  Trash2,
  Edit3,
  CheckCircle,
  Mail,
  Phone,
  X,
  Eye,
  BarChart3,
  Users,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";
import api, { COMPANY } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TABS = [
  { key: "stats", label: "Στατιστικά", icon: BarChart3 },
  { key: "jobs", label: "Αγγελίες", icon: Briefcase },
  { key: "messages", label: "Μηνύματα", icon: Inbox },
];

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("stats");

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [user, loading, navigate]);

  if (loading || !user) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center text-[#A1A1AA]">
        Φόρτωση...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Top bar */}
      <header className="border-b border-[#27272A] bg-[#0F0F11]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src={COMPANY.logo} alt="logo" className="h-8 w-auto" />
            <div>
              <div className="font-heading text-sm text-white font-semibold">
                Admin Panel
              </div>
              <div className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#E63946]">
                Control Room
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:inline text-xs font-mono-th text-[#A1A1AA]">
              {user.email}
            </span>
            <button
              onClick={() => {
                logout();
                navigate("/admin/login");
              }}
              data-testid="admin-logout-btn"
              className="text-sm text-[#A1A1AA] hover:text-[#E63946] inline-flex items-center gap-2 transition-colors"
            >
              <LogOut size={14} /> Αποσύνδεση
            </button>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-px bg-[#27272A]">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              data-testid={`admin-tab-${t.key}`}
              className={`px-5 py-3 text-sm font-medium inline-flex items-center gap-2 transition-colors ${
                tab === t.key
                  ? "bg-[#050505] text-white border-t-2 border-[#E63946]"
                  : "bg-[#0F0F11] text-[#A1A1AA] hover:text-white"
              }`}
            >
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {tab === "stats" ? <StatsPanel /> : tab === "jobs" ? <JobsPanel /> : <MessagesPanel />}
      </main>
    </div>
  );
}

// -------- Stats Panel --------
function StatsPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/stats")
      .then((r) => setData(r.data))
      .catch(() => toast.error("Σφάλμα φόρτωσης στατιστικών"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="text-[#A1A1AA] text-sm">Φόρτωση...</div>;
  }
  if (!data) return null;

  const days = data.last_30_days || [];
  const maxCount = Math.max(1, ...days.map((d) => d.count));

  // Build a display array of last 30 days including zeros
  const buildLast30 = () => {
    const map = new Map(days.map((d) => [d.date, d.count]));
    const out = [];
    const today = new Date(data.today_date + "T12:00:00Z");
    for (let i = 29; i >= 0; i--) {
      const d = new Date(today);
      d.setUTCDate(d.getUTCDate() - i);
      const iso = d.toISOString().slice(0, 10);
      out.push({ date: iso, count: map.get(iso) || 0 });
    }
    return out;
  };
  const series = buildLast30();
  const max30 = Math.max(1, ...series.map((s) => s.count));

  return (
    <div data-testid="admin-stats-panel">
      <div className="mb-6">
        <div className="overline">Επισκόπηση</div>
        <h2 className="font-heading text-2xl text-white font-medium tracking-tight">
          Στατιστικά Επισκεψιμότητας
        </h2>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[#27272A] border border-[#27272A]">
        <Kpi
          icon={Users}
          label="Επισκέπτες Σήμερα"
          value={data.today}
          hint={data.today_date}
          testid="kpi-today"
        />
        <Kpi
          icon={TrendingUp}
          label="Συνολικοί Επισκέπτες"
          value={data.total}
          hint="Από την έναρξη"
          testid="kpi-total"
        />
        <Kpi
          icon={Briefcase}
          label="Ενεργές Αγγελίες"
          value={data.jobs_active}
          hint={`Σύνολο: ${data.jobs_total}`}
          testid="kpi-jobs"
        />
        <Kpi
          icon={Inbox}
          label="Μηνύματα"
          value={data.messages_total}
          hint={`Αδιάβαστα: ${data.messages_unread}`}
          accent={data.messages_unread > 0}
          testid="kpi-messages"
        />
      </div>

      {/* 30-day bar chart */}
      <div className="mt-10 border border-[#27272A] bg-[#0F0F11] p-6 sm:p-8">
        <div className="flex flex-wrap items-end justify-between gap-2 mb-6">
          <div>
            <div className="overline">Επισκέπτες ανά Ημέρα</div>
            <div className="font-heading text-lg text-white mt-1">
              Τελευταίες 30 ημέρες
            </div>
          </div>
          <div className="font-mono-th text-xs text-[#A1A1AA]">
            μέγιστο: <span className="text-white">{max30}</span>
          </div>
        </div>

        <div className="flex items-end gap-1 h-48" data-testid="visits-chart">
          {series.map((s) => {
            const h = Math.round((s.count / max30) * 100);
            const isToday = s.date === data.today_date;
            return (
              <div
                key={s.date}
                className="flex-1 group relative flex items-end"
                style={{ minHeight: "1px" }}
                title={`${s.date}: ${s.count} επισκέπτες`}
              >
                <div
                  className={`w-full transition-all duration-300 ${
                    isToday
                      ? "bg-[#E63946]"
                      : s.count > 0
                      ? "bg-[#E63946]/40 group-hover:bg-[#E63946]/70"
                      : "bg-[#27272A]"
                  }`}
                  style={{ height: `${Math.max(h, s.count > 0 ? 4 : 2)}%` }}
                />
                <div className="absolute -top-7 left-1/2 -translate-x-1/2 bg-[#16161A] border border-[#27272A] px-2 py-1 text-[10px] text-white opacity-0 group-hover:opacity-100 pointer-events-none whitespace-nowrap font-mono-th">
                  {s.date.slice(5)} · {s.count}
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between mt-3 font-mono-th text-[10px] text-[#A1A1AA]">
          <span>{series[0].date.slice(5)}</span>
          <span>{series[Math.floor(series.length / 2)].date.slice(5)}</span>
          <span>σήμερα</span>
        </div>
      </div>

      {/* Daily list (most recent first) */}
      <div className="mt-10">
        <div className="overline mb-4">Αναλυτικά</div>
        <div className="border border-[#27272A] divide-y divide-[#27272A] max-h-96 overflow-y-auto">
          {[...series].reverse().filter((s) => s.count > 0).length === 0 ? (
            <div className="p-6 text-center text-[#A1A1AA] text-sm">
              Δεν έχουν καταγραφεί επισκέψεις ακόμη.
            </div>
          ) : (
            [...series].reverse().map((s) => (
              <div
                key={s.date}
                className="px-5 py-3 flex items-center justify-between bg-[#0F0F11] hover:bg-[#16161A] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span
                    className={`w-2 h-2 ${
                      s.date === data.today_date
                        ? "bg-[#E63946]"
                        : s.count > 0
                        ? "bg-[#E63946]/50"
                        : "bg-[#27272A]"
                    }`}
                  />
                  <span className="font-mono-th text-xs text-[#A1A1AA]">
                    {s.date}
                    {s.date === data.today_date && (
                      <span className="ml-2 text-[#E63946]">(σήμερα)</span>
                    )}
                  </span>
                </div>
                <div className="font-heading text-base text-white tabular-nums">
                  {s.count} <span className="text-xs text-[#A1A1AA] ml-1">επισκέπτες</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, hint, accent, testid }) {
  return (
    <div className="bg-[#0F0F11] p-5 sm:p-6" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA]">
          {label}
        </div>
        <Icon size={16} className={accent ? "text-[#E63946]" : "text-[#A1A1AA]"} />
      </div>
      <div className="font-heading text-3xl sm:text-4xl text-white font-medium tracking-tighter tabular-nums">
        {value}
      </div>
      {hint && (
        <div className="mt-1 font-mono-th text-[10px] text-[#A1A1AA]">{hint}</div>
      )}
    </div>
  );
}

// -------- Jobs Panel --------
function JobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | job object

  const load = () => {
    setLoading(true);
    api
      .get("/admin/jobs")
      .then((r) => setJobs(r.data))
      .catch(() => toast.error("Σφάλμα φόρτωσης αγγελιών"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onDelete = async (id) => {
    if (!window.confirm("Διαγραφή αγγελίας;")) return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success("Διαγράφηκε");
      load();
    } catch {
      toast.error("Σφάλμα διαγραφής");
    }
  };

  const onToggle = async (job) => {
    try {
      await api.patch(`/admin/jobs/${job.id}`, { active: !job.active });
      load();
    } catch {
      toast.error("Σφάλμα");
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="overline">Αγγελίες Καριέρας</div>
          <h2 className="font-heading text-2xl text-white font-medium tracking-tight">
            Διαχείριση Θέσεων
          </h2>
        </div>
        <button
          onClick={() => setEditing("new")}
          data-testid="add-job-btn"
          className="btn-primary px-5 py-2.5 text-sm font-medium inline-flex items-center gap-2"
        >
          <Plus size={16} /> Νέα Αγγελία
        </button>
      </div>

      {loading ? (
        <div className="text-[#A1A1AA] text-sm">Φόρτωση...</div>
      ) : jobs.length === 0 ? (
        <div className="border border-[#27272A] py-16 text-center text-[#A1A1AA]">
          Δεν υπάρχουν αγγελίες ακόμη.
        </div>
      ) : (
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {jobs.map((j) => (
            <div
              key={j.id}
              className="px-5 py-4 grid grid-cols-12 gap-4 items-center bg-[#0F0F11] hover:bg-[#16161A] transition-colors"
            >
              <div className="col-span-12 md:col-span-6">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`w-2 h-2 ${j.active ? "bg-[#E63946]" : "bg-[#A1A1AA]"}`}
                  />
                  <span className="font-mono-th text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
                    {j.active ? "Ενεργή" : "Ανενεργή"} · {j.employment_type}
                  </span>
                </div>
                <div className="font-heading text-base text-white font-medium">
                  {j.title}
                </div>
                <div className="text-xs text-[#A1A1AA]">{j.location}</div>
              </div>
              <div className="col-span-12 md:col-span-6 flex flex-wrap gap-2 justify-start md:justify-end">
                <button
                  onClick={() => onToggle(j)}
                  data-testid={`toggle-job-${j.id}`}
                  className="text-xs text-[#A1A1AA] hover:text-white border border-[#27272A] px-3 py-2 inline-flex items-center gap-1"
                >
                  <CheckCircle size={12} /> {j.active ? "Απενεργοποίηση" : "Ενεργοποίηση"}
                </button>
                <button
                  onClick={() => setEditing(j)}
                  data-testid={`edit-job-${j.id}`}
                  className="text-xs text-white border border-[#27272A] hover:border-white px-3 py-2 inline-flex items-center gap-1"
                >
                  <Edit3 size={12} /> Επεξεργασία
                </button>
                <button
                  onClick={() => onDelete(j.id)}
                  data-testid={`delete-job-${j.id}`}
                  className="text-xs text-[#E63946] border border-[#E63946]/40 hover:bg-[#E63946]/10 px-3 py-2 inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Διαγραφή
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <JobEditor
          job={editing === "new" ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}
    </div>
  );
}

function JobEditor({ job, onClose, onSaved }) {
  const [form, setForm] = useState(
    job || {
      title: "",
      location: "Άγιοι Ανάργυροι",
      employment_type: "Πλήρης Απασχόληση",
      description: "",
      requirements: "",
      active: true,
    }
  );
  const [saving, setSaving] = useState(false);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === "checkbox" ? checked : value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (job) {
        await api.patch(`/admin/jobs/${job.id}`, form);
        toast.success("Ενημερώθηκε");
      } else {
        await api.post("/admin/jobs", form);
        toast.success("Δημιουργήθηκε");
      }
      onSaved();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Σφάλμα αποθήκευσης");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
      <form
        onSubmit={onSubmit}
        data-testid="job-editor-form"
        className="bg-[#0F0F11] border border-[#27272A] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <div className="px-6 py-5 border-b border-[#27272A] flex items-center justify-between">
          <h3 className="font-heading text-lg text-white font-medium">
            {job ? "Επεξεργασία Αγγελίας" : "Νέα Αγγελία"}
          </h3>
          <button type="button" onClick={onClose} className="text-[#A1A1AA] hover:text-white">
            <X size={18} />
          </button>
        </div>

        <div className="px-6 py-6 space-y-5">
          <Field
            label="Τίτλος"
            name="title"
            required
            value={form.title}
            onChange={onChange}
            testid="job-input-title"
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Field
              label="Τοποθεσία"
              name="location"
              required
              value={form.location}
              onChange={onChange}
              testid="job-input-location"
            />
            <Field
              label="Τύπος Απασχόλησης"
              name="employment_type"
              value={form.employment_type}
              onChange={onChange}
              testid="job-input-type"
            />
          </div>
          <TextArea
            label="Περιγραφή"
            name="description"
            required
            rows={5}
            value={form.description}
            onChange={onChange}
            testid="job-input-description"
          />
          <TextArea
            label="Απαιτήσεις (προαιρετικό)"
            name="requirements"
            rows={4}
            value={form.requirements}
            onChange={onChange}
            testid="job-input-requirements"
          />
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              name="active"
              checked={form.active}
              onChange={onChange}
              className="accent-[#E63946] w-4 h-4"
              data-testid="job-input-active"
            />
            <span className="text-sm text-white">Ενεργή αγγελία (ορατή στο website)</span>
          </label>
        </div>

        <div className="px-6 py-5 border-t border-[#27272A] flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-[#A1A1AA] hover:text-white"
          >
            Ακύρωση
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="job-save-btn"
            className="btn-primary px-6 py-2.5 text-sm font-medium disabled:opacity-60"
          >
            {saving ? "Αποθήκευση..." : "Αποθήκευση"}
          </button>
        </div>
      </form>
    </div>
  );
}

// -------- Messages Panel --------
function MessagesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/contact")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Σφάλμα φόρτωσης"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const onMarkRead = async (id) => {
    try {
      await api.patch(`/admin/contact/${id}/read`);
      load();
    } catch {
      toast.error("Σφάλμα");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Διαγραφή μηνύματος;")) return;
    try {
      await api.delete(`/admin/contact/${id}`);
      toast.success("Διαγράφηκε");
      load();
      setSelected(null);
    } catch {
      toast.error("Σφάλμα διαγραφής");
    }
  };

  return (
    <div>
      <div className="mb-6">
        <div className="overline">Επικοινωνία</div>
        <h2 className="font-heading text-2xl text-white font-medium tracking-tight">
          Φόρμα Πελατών{" "}
          <span className="text-[#A1A1AA] text-base">({items.length})</span>
        </h2>
      </div>

      {loading ? (
        <div className="text-[#A1A1AA] text-sm">Φόρτωση...</div>
      ) : items.length === 0 ? (
        <div className="border border-[#27272A] py-16 text-center text-[#A1A1AA]">
          Δεν υπάρχουν μηνύματα.
        </div>
      ) : (
        <div className="border border-[#27272A] divide-y divide-[#27272A]">
          {items.map((m) => (
            <div
              key={m.id}
              className="px-5 py-4 grid grid-cols-12 gap-4 items-center bg-[#0F0F11] hover:bg-[#16161A] transition-colors"
            >
              <div className="col-span-12 md:col-span-7">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`w-2 h-2 ${m.read ? "bg-[#A1A1AA]" : "bg-[#E63946]"}`}
                  />
                  <span className="font-mono-th text-[10px] tracking-[0.2em] uppercase text-[#A1A1AA]">
                    {m.read ? "Διαβάστηκε" : "Νέο"} ·{" "}
                    {new Date(m.created_at).toLocaleString("el-GR")}
                  </span>
                </div>
                <div className="font-heading text-base text-white font-medium">
                  {m.name} — {m.subject}
                </div>
                <div className="text-xs text-[#A1A1AA] truncate">{m.email} · {m.phone || "-"}</div>
              </div>
              <div className="col-span-12 md:col-span-5 flex flex-wrap gap-2 justify-start md:justify-end">
                <button
                  onClick={() => {
                    setSelected(m);
                    if (!m.read) onMarkRead(m.id);
                  }}
                  data-testid={`view-msg-${m.id}`}
                  className="text-xs text-white border border-[#27272A] hover:border-white px-3 py-2 inline-flex items-center gap-1"
                >
                  <Eye size={12} /> Προβολή
                </button>
                <button
                  onClick={() => onDelete(m.id)}
                  data-testid={`delete-msg-${m.id}`}
                  className="text-xs text-[#E63946] border border-[#E63946]/40 hover:bg-[#E63946]/10 px-3 py-2 inline-flex items-center gap-1"
                >
                  <Trash2 size={12} /> Διαγραφή
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div
          className="fixed inset-0 z-[60] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setSelected(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#0F0F11] border border-[#27272A] w-full max-w-2xl max-h-[90vh] overflow-y-auto"
          >
            <div className="px-6 py-5 border-b border-[#27272A] flex items-center justify-between">
              <div>
                <div className="overline">Μήνυμα</div>
                <h3 className="font-heading text-lg text-white font-medium">
                  {selected.subject}
                </h3>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="text-[#A1A1AA] hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="px-6 py-6 space-y-4">
              <Row label="Όνομα" value={selected.name} />
              <Row
                label="Email"
                value={
                  <a
                    href={`mailto:${selected.email}`}
                    className="text-[#E63946] hover:underline inline-flex items-center gap-2"
                  >
                    <Mail size={12} /> {selected.email}
                  </a>
                }
              />
              <Row
                label="Τηλέφωνο"
                value={
                  selected.phone ? (
                    <a
                      href={`tel:${selected.phone}`}
                      className="text-white hover:text-[#E63946] inline-flex items-center gap-2"
                    >
                      <Phone size={12} /> {selected.phone}
                    </a>
                  ) : (
                    "-"
                  )
                }
              />
              <Row
                label="Ημερομηνία"
                value={new Date(selected.created_at).toLocaleString("el-GR")}
              />
              <div className="pt-2">
                <div className="overline mb-3">Μήνυμα</div>
                <p className="text-white/90 leading-relaxed whitespace-pre-wrap text-sm">
                  {selected.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="grid grid-cols-3 gap-4 py-2 border-b border-[#27272A] last:border-0">
      <div className="font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] pt-1">
        {label}
      </div>
      <div className="col-span-2 text-white text-sm">{value}</div>
    </div>
  );
}

function Field({ label, testid, ...props }) {
  return (
    <div>
      <label className="block font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">
        {label} {props.required && "*"}
      </label>
      <input
        {...props}
        data-testid={testid}
        className="input-th w-full px-4 py-3 text-sm rounded-none"
      />
    </div>
  );
}

function TextArea({ label, testid, ...props }) {
  return (
    <div>
      <label className="block font-mono-th text-[10px] uppercase tracking-[0.2em] text-[#A1A1AA] mb-2">
        {label} {props.required && "*"}
      </label>
      <textarea
        {...props}
        data-testid={testid}
        className="input-th w-full px-4 py-3 text-sm resize-none rounded-none"
      />
    </div>
  );
}
